import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Logo } from "@/components/Logo";

export const Route = createFileRoute("/reset-password")({
  head: () => ({ meta: [{ title: "Redefinir senha — Viny Club" }] }),
  component: ResetPassword,
});

function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [valid, setValid] = useState<boolean | null>(null);

  useEffect(() => {
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace("#", ""));
    if (params.get("type") === "recovery") {
      setValid(true);
    } else {
      setValid(false);
    }
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("A senha precisa ter pelo menos 6 caracteres");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success("Senha redefinida com sucesso!");
      navigate({ to: "/auth" });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erro ao redefinir senha";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="grid min-h-screen bg-black text-white">
      <div className="relative flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm">
          <div className="mb-10 flex items-center gap-2">
            <Logo variant="light" size={40} />
          </div>

          <Link
            to="/auth"
            className="inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.3em] text-white/60 transition-colors hover:text-white"
          >
            ← Voltar para o login
          </Link>

          {valid === null && (
            <div className="mt-8 flex items-center gap-2 text-sm text-white/50">
              <Loader2 className="h-4 w-4 animate-spin" />
              Verificando link...
            </div>
          )}

          {valid === false && (
            <div className="mt-8">
              <h1 className="font-display text-2xl font-medium tracking-[-0.02em]">
                Link inválido ou expirado
              </h1>
              <p className="mt-2 text-sm text-white/50">
                Solicite um novo link de redefinição de senha.
              </p>
              <Link
                to="/auth"
                className="mt-6 inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-bold text-black shadow-xl transition-transform hover:scale-[1.02]"
              >
                Ir para o login
              </Link>
            </div>
          )}

          {valid === true && (
            <>
              <h1 className="mt-5 font-display text-4xl font-medium leading-[1] tracking-[-0.03em]">
                Nova{" "}
                <em className="font-serif italic font-normal text-white/70">
                  senha
                </em>
              </h1>
              <p className="mt-3 text-sm text-white/50">
                Escolha uma nova senha para sua conta.
              </p>

              <form className="mt-8 space-y-3" onSubmit={handleSubmit}>
                <input
                  type="password"
                  placeholder="Nova senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="h-12 w-full rounded-full border border-white/10 bg-white/[0.04] px-5 text-sm text-white placeholder-white/40 backdrop-blur-xl focus:border-white/40 focus:outline-none"
                />
                <input
                  type="password"
                  placeholder="Confirmar nova senha"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className="h-12 w-full rounded-full border border-white/10 bg-white/[0.04] px-5 text-sm text-white placeholder-white/40 backdrop-blur-xl focus:border-white/40 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="flex h-12 w-full items-center justify-center rounded-full bg-white text-sm font-bold text-black shadow-xl shadow-black/40 transition-transform hover:scale-[1.02] disabled:opacity-60"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Redefinir senha"
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
